// Shared macro & crypto event calendar — single source of truth for
// CalendarTab and the DashboardTab "next event" widget.
//
// Dates are taken from the official publishers' schedules (verified 2026-09-23):
//   FOMC      federalreserve.gov/monetarypolicy/fomccalendars.htm   (2026–2027)
//   CPI/NFP/PPI bls.gov/schedule/news_release/{cpi,empsit,ppi}.htm  (2026 only — 2027 not yet published)
//   PCE/GDP   bea.gov/news/schedule                                  (2026 only — 2027 not yet published)
//   Retail    census.gov/economic-indicators                          (2026 only — 2027 not yet published)
//   Michigan  data.sca.isr.umich.edu release-dates PDF (final)       (2026–2027)
//   ECB       ecb.europa.eu/press/calendars/mgcgc                    (2026–2027)
//   BoE       bankofengland.co.uk/monetary-policy/upcoming-mpc-dates (2026–2027)
//   CME       CME Rulebook ch. 350: last Friday of the month, 16:00 London,
//             preceding UK+US business day if that Friday is a holiday
//   ETF opts  3rd Friday of Mar/Jun/Sep/Dec, 16:00 ET; preceding day on NYSE holidays
//
// forecast / previous / actual are intentionally left empty for macro releases:
// only fill them from a reliable source, never with estimates.
//
// When adding the 2027 BLS/BEA/Census dates, update CALENDAR_DATA_UPDATED too.

import { newYorkToUtc as newYork, londonToUtc as london, frankfurtToUtc as frankfurt } from '../utils/timezone';

export const CALENDAR_DATA_UPDATED = '2026-09-23';

// ── Event builders ────────────────────────────────────────────────────────────

const HU_MONTHS = ['január','február','március','április','május','június','július','augusztus','szeptember','október','november','december'];
const refMonth = date => HU_MONTHS[(Number(date.slice(5, 7)) + 10) % 12]; // month before release
// Keeps the legacy id format (e.g. 'cpi-jun26') so saved event notes (cal_notes) stay attached
const MON_IDS = ['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'];
const idSuffix = date => `${MON_IDS[Number(date.slice(5, 7)) - 1]}${date.slice(2, 4)}`;
const quarterId = date => `q${Math.ceil(Number(date.slice(5, 7)) / 3)}-${date.slice(2, 4)}`;

const series = (dates, build) => dates.map(build);

const FOMC = series(
  ['2026-06-17','2026-07-29','2026-09-16','2026-10-28','2026-12-09',
   '2027-01-27','2027-03-17','2027-04-28','2027-06-09','2027-07-28','2027-09-15','2027-10-27','2027-12-08'],
  date => ({ id: `fomc-${idSuffix(date)}`, date, time: newYork(date, '14:00'), event: 'FOMC — Fed Kamatdöntés', category: 'macro-us', impact: 'high' }),
);

const CPI = series(
  ['2026-05-12','2026-06-10','2026-07-14','2026-08-12','2026-09-11','2026-10-14','2026-11-10','2026-12-10'],
  date => ({ id: `cpi-${idSuffix(date)}`, date, time: newYork(date, '08:30'), event: `CPI — US Fogyasztói Árindex (${refMonth(date)})`, category: 'macro-us', impact: 'high', lowerIsBetter: true }),
);

const NFP = series(
  ['2026-05-08','2026-06-05','2026-07-02','2026-08-07','2026-09-04','2026-10-02','2026-11-06','2026-12-04'],
  date => ({ id: `nfp-${idSuffix(date)}`, date, time: newYork(date, '08:30'), event: `NFP — Munkaerőpiaci Adat (${refMonth(date)})`, category: 'macro-us', impact: 'high' }),
);

const PPI = series(
  ['2026-05-13','2026-06-11','2026-07-15','2026-08-13','2026-09-10','2026-10-15','2026-11-13','2026-12-15'],
  date => ({ id: `ppi-${idSuffix(date)}`, date, time: newYork(date, '08:30'), event: 'PPI — Termelői Árindex', category: 'macro-us', impact: 'medium', lowerIsBetter: true }),
);

const PCE = series(
  ['2026-05-28','2026-06-25','2026-07-30','2026-08-26','2026-09-30','2026-10-29','2026-11-25','2026-12-23'],
  date => ({ id: `pce-${idSuffix(date)}`, date, time: newYork(date, '08:30'), event: 'PCE Árindex (Fed preferált inflációs mutató)', category: 'macro-us', impact: 'high', lowerIsBetter: true }),
);

const GDP = [
  { id: 'gdp-q2-26', date: '2026-07-30', event: 'Q2 2026 GDP — Előzetes Becslés' },
  { id: 'gdp-q3-26', date: '2026-10-29', event: 'Q3 2026 GDP — Előzetes Becslés' },
].map(e => ({ ...e, time: newYork(e.date, '08:30'), category: 'macro-us', impact: 'medium' }));

const RETAIL = series(
  ['2026-05-14','2026-06-17','2026-07-16','2026-08-14','2026-09-16','2026-10-15','2026-11-17','2026-12-16'],
  date => ({ id: `retail-${idSuffix(date)}`, date, time: newYork(date, '08:30'), event: 'US Kiskereskedelmi Eladások', category: 'macro-us', impact: 'medium' }),
);

// University of Michigan — final monthly reading
const MICHIGAN = series(
  ['2026-05-22','2026-06-26','2026-07-31','2026-08-28','2026-09-25','2026-10-23','2026-11-20','2026-12-18',
   '2027-01-22','2027-02-26','2027-03-25','2027-04-23','2027-05-21','2027-06-25',
   '2027-07-30','2027-08-27','2027-09-24','2027-10-22','2027-11-19','2027-12-17'],
  date => ({ id: `michigan-${idSuffix(date)}`, date, time: newYork(date, '10:00'), event: 'Michigan Fogyasztói Bizalom (végleges)', category: 'macro-us', impact: 'low' }),
);

const ECB = series(
  ['2026-06-11','2026-07-23','2026-09-10','2026-10-29','2026-12-17',
   '2027-02-04','2027-03-18','2027-04-29','2027-06-10','2027-07-22','2027-09-09','2027-10-28','2027-12-16'],
  date => ({ id: `ecb-${idSuffix(date)}`, date, time: frankfurt(date, '14:15'), event: 'EKB Kamatdöntés', category: 'macro-eu', impact: 'medium' }),
);

const BOE = series(
  ['2026-06-18','2026-07-30','2026-09-17','2026-11-05','2026-12-17',
   '2027-02-04','2027-03-18','2027-04-29','2027-06-17','2027-07-29','2027-09-16','2027-11-04','2027-12-16'],
  date => ({ id: `boe-${idSuffix(date)}`, date, time: london(date, '12:00'), event: 'Bank of England Kamatdöntés', category: 'macro-eu', impact: 'low' }),
);

// Last Friday of the month; 2026-12 (Christmas) and 2027-03 (Good Friday) roll back one day
const CME = series(
  ['2026-05-29','2026-06-26','2026-07-31','2026-08-28','2026-09-25','2026-10-30','2026-11-27','2026-12-24',
   '2027-01-29','2027-02-26','2027-03-25','2027-04-30','2027-05-28','2027-06-25',
   '2027-07-30','2027-08-27','2027-09-24','2027-10-29','2027-11-26','2027-12-31'],
  date => ({ id: `cme-${idSuffix(date)}`, date, time: london(date, '16:00'), event: 'CME Bitcoin Futures Lejárat', category: 'crypto', impact: 'medium' }),
);

// Quarterly options expiry; 2026-06 and 2027-06 fall on Juneteenth → Thursday
const ETF_OPTIONS = series(
  ['2026-06-18','2026-09-18','2026-12-18','2027-03-19','2027-06-17','2027-09-17','2027-12-17'],
  date => ({ id: `etf-${quarterId(date)}`, date, time: newYork(date, '16:00'), event: 'Negyedéves BTC/ETH ETF Opciók Lejárata', category: 'etf', impact: 'high' }),
);

const HALVING = [
  // Block 1 050 000 — date is a block-time estimate, not a scheduled calendar date
  { id: 'btc-halving-28', date: '2028-04-17', time: '00:00 UTC', event: 'Bitcoin Halving — Blokk 1 050 000 (becsült dátum)', category: 'crypto', impact: 'high', forecast: 'Jutalom → 1.5625 BTC', previous: '3.125 BTC/blokk' },
];

export const EVENTS = [
  ...FOMC, ...CPI, ...NFP, ...PPI, ...PCE, ...GDP, ...RETAIL, ...MICHIGAN,
  ...ECB, ...BOE, ...CME, ...ETF_OPTIONS, ...HALVING,
];

// Recurring series by id prefix — used to flag series whose next date is not in the
// calendar yet (e.g. BLS/BEA publish the next year's schedule only in late autumn)
export const SERIES_LABELS = {
  fomc: 'FOMC', cpi: 'CPI', nfp: 'NFP', ppi: 'PPI', pce: 'PCE', gdp: 'GDP',
  retail: 'Kiskereskedelem', michigan: 'Michigan', ecb: 'EKB', boe: 'BoE',
  cme: 'CME', etf: 'ETF-opciók',
};

export function missingSeries(upcomingEvents) {
  return Object.entries(SERIES_LABELS)
    .filter(([prefix]) => !upcomingEvents.some(e => e.id.startsWith(`${prefix}-`)))
    .map(([, label]) => label);
}

// ── Config ─────────────────────────────────────────────────────────────────────

export const CAT = {
  'macro-us': { label: 'Makró USA',     icon: '🇺🇸', color: 'var(--blue)'     },
  'macro-eu': { label: 'Makró EU/UK',  icon: '🇪🇺', color: '#9b59b6'        },
  'crypto':   { label: 'Kripto',       icon: '₿',   color: 'var(--positive)' },
  'etf':      { label: 'ETF/Opciók',   icon: '📊',  color: 'var(--accent)'   },
};
